import "./Header.less";
import strings from "./Header.strings";
import { selectForm } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { useAppDispatch, useAppSelector } from "app/hooks";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import { useStrings } from "features/i18n/useStrings";
import {
    GuxButtonMulti,
    GuxIcon,
    GuxListItem,
} from "genesys-spark-components-react";
import embeddingService from "services/embeddingService";
import { selectScorable, selectVersionEntitiesSorted } from "./scorableSlice";
import { EvaluationVersionStatusEnum } from "@genesys/cloud-frontend/dist/mjs/models/EvaluationVersion";
import setContextRoute from "react/utils/setContextRoute";
import { scorableSliceActions } from "./scorableSlice";
import { DateTimeWithTimeZone } from "features/i18n/DateTime";

function VersionDropdown({
    mappedVersionHistoryEntities,
}: {
    mappedVersionHistoryEntities: JSX.Element[];
}) {
    const t = useStrings(strings);
    return (
        <GuxButtonMulti
            className="EvaluationVersion"
            accent="tertiary"
            itemScope={true}
            isOpen={false}
            data-testid="evaluation-version-button"
        >
            <span slot="title">
                {t.labels.evaluationVersions({
                    evaluationVersions:
                        mappedVersionHistoryEntities.length ?? 0,
                })}
            </span>
            {mappedVersionHistoryEntities}
        </GuxButtonMulti>
    );
}

function MappedVersionHistoryEntities({ isAdmin }: { isAdmin: boolean }) {
    const t = useStrings(strings);
    const VersionHistoryEntities = useAppSelector(selectVersionEntitiesSorted);
    const scorableId = useAppSelector((state) => selectScorable(state)?.id);
    const scorableConversationId = useAppSelector(
        (state) => selectScorable(state)?.conversation?.id
    );
    const isAssigneeAndDisputesFTEnabled = useAppSelector((state) => {
        return (
            selectFeatureToggle(
                state,
                FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
            ) && selectFeatureToggle(state, FeatureToggle.DISPUTES_TOGGLE)
        );
    });
    return VersionHistoryEntities.flatMap((version, index) => {
        const isCurrent =
            version.status !== EvaluationVersionStatusEnum.RETRACTED &&
            index === 0;
        const versionName = isCurrent
            ? t.labels.currentVersion
            : t.labels.retractedVersion({
                  retractedVersion: version.version ?? 0,
              });
        const selected = version.id === scorableId;
        const incomplete =
            version.status === EvaluationVersionStatusEnum.INPROGRESS;
        if (!isAdmin && incomplete) return [];
        return (
            <GuxListItem
                className="VersionDropdownItem"
                key={version.id}
                onClick={() => {
                    if (!scorableConversationId || !version.id) return;
                    setContextRoute(
                        scorableConversationId,
                        version.id,
                        isAdmin,
                        isAssigneeAndDisputesFTEnabled ? false : undefined
                    );
                }}
            >
                <div className={`VersionName${selected ? " Current" : ""}`}>
                    {versionName}
                </div>
                <div
                    className="VersionStatus"
                    data-testid={`version-status-${index}`}
                >
                    {incomplete || !version.submittedDate
                        ? t.labels.unsubmitted
                        : t.labels.submitted}
                </div>
                {version.submittedDate && (
                    <div
                        className="VersionDate"
                        data-testid={`version-date-${index}`}
                    >
                        <DateTimeWithTimeZone date={version.submittedDate} />
                    </div>
                )}
            </GuxListItem>
        );
    });
}

export function Header({ isAdmin }: { isAdmin?: boolean }) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const formName = useAppSelector((state) => selectForm(state)?.name);

    const mappedVersionHistoryEntities = MappedVersionHistoryEntities({
        isAdmin: isAdmin ?? false,
    });

    return (
        <div className="Header">
            <h1 className="gux-heading-lg-bold">{formName}</h1>
            <div className="right-hand-side">
                {mappedVersionHistoryEntities &&
                    mappedVersionHistoryEntities.length > 1 && (
                        <VersionDropdown
                            mappedVersionHistoryEntities={
                                mappedVersionHistoryEntities
                            }
                        />
                    )}
                <GuxIcon
                    iconName="close"
                    className="CloseButton"
                    screenreaderText={t.labels.closeSidePanel}
                    onClick={() => {
                        embeddingService.requestCloseEvaluation();
                        dispatch(
                            scorableSliceActions.setUpdateEvaluationError({})
                        );
                    }}
                />
            </div>
        </div>
    );
}
